/**
 * Une carte du deck. (Brief §3.3)
 *
 * Parti pris : **format fixe**, comme une carte physique. Toutes les cartes ont
 * le même gabarit quelle que soit la longueur du tweet — le texte est tronqué,
 * la zone média a une hauteur constante. Un deck où les cartes changent de
 * taille ne se manipule pas au pouce : la cible bouge sous le doigt.
 *
 * Le rendu 3D est fait en transforms CSS, pas en WebGL. Perspective,
 * inclinaison, reflet spéculaire et profondeur d'ombre suffisent à donner la
 * sensation d'un objet physique, pour zéro dépendance. three.js ne se
 * justifierait qu'avec une vraie épaisseur de carte ou un éclairage dynamique.
 *
 * Le seuil de validation combine distance ET vélocité : un flick court et
 * rapide valide, un glissement lent et long aussi. La distance seule rend le
 * geste mou, la vélocité seule empêche de placer la carte précisément.
 */

import {
  motion,
  useMotionValue,
  useMotionTemplate,
  useTransform,
  type PanInfo,
} from "framer-motion";
import type { DeckCard } from "@rm/shared";

import { avatarForSize } from "../lib/avatar.js";
import { Badge } from "./ui.js";

const DISTANCE_THRESHOLD = 110;
const VELOCITY_THRESHOLD = 500;

/**
 * Ressort de chute. `damping` volontairement bas : la carte dépasse un peu sa
 * position puis se replace, ce qui donne le poids. Un amortissement critique
 * ferait glisser la carte au lieu de la faire tomber.
 */
const SPRING = { type: "spring", stiffness: 260, damping: 21, mass: 0.85 } as const;

/** Décalage entre deux cartes de la pile, en secondes. */
const DEAL_STAGGER = 0.045;

/** Profondeur maximale rendue — au-delà, rien n'est visible. */
const MAX_DEPTH = 2;

/**
 * La matière d'une carte : dégradé de fond et empilement d'ombres.
 *
 * Exportée pour que la shortlist utilise exactement la même — deux surfaces
 * légèrement différentes se liraient comme deux objets de natures différentes,
 * alors que c'est le même tweet vu à deux endroits.
 *
 * Les trois ombres ont des netteté décroissantes : contact, diffusion, halo.
 * C'est ce dégradé qui fait lire l'objet comme épais.
 */
export const CARD_SURFACE = {
  background: "linear-gradient(168deg, #1d1d1f 0%, #161617 46%, #101011 100%)",
  boxShadow:
    "0 1px 0 0 rgba(255,255,255,0.07) inset, " +
    "0 0 0 1px rgba(255,255,255,0.06), " +
    "0 2px 4px rgba(0,0,0,0.5), 0 12px 24px rgba(0,0,0,0.45), 0 28px 56px rgba(0,0,0,0.35)",
} as const;

/**
 * Le score en pastille, comme le coût d'une carte de jeu : c'est l'information
 * qu'on lit en premier, elle a droit à sa gemme.
 */
export function ScoreGem({ score, size = 40 }: { score: number; size?: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full font-semibold tabular-nums"
      style={{
        width: size,
        height: size,
        fontSize: size <= 32 ? 12 : 14,
        background: "radial-gradient(circle at 35% 30%, #3a4a6b, #1b2437)",
        boxShadow:
          "0 0 0 1px rgba(255,255,255,0.12), 0 2px 6px rgba(0,0,0,0.5), " +
          "0 1px 0 rgba(255,255,255,0.18) inset",
      }}
    >
      {Math.round(score)}
    </div>
  );
}

/** L'avatar, avec le liseré qui le détache du fond de la carte. */
export function Avatar({ url, size = 36 }: { url?: string | null; size?: number }) {
  const cls = "shrink-0 rounded-full ring-1 ring-white/10";
  // L'API ne donne que la variante 48 px : on demande au CDN celle qui couvre
  // la taille physique réellement rendue — cf. lib/avatar.ts.
  return url ? (
    <img
      src={avatarForSize(url, size) ?? undefined}
      alt=""
      className={cls}
      style={{ width: size, height: size }}
      loading="lazy"
    />
  ) : (
    <div className={`${cls} bg-white/5`} style={{ width: size, height: size }} />
  );
}

export interface SwipeCardProps {
  card: DeckCard;
  /** Position dans la pile : 0 = carte du dessus, seule manipulable. */
  depth: number;
  /**
   * Direction de sortie imposée. Nécessaire pour les boutons Pass/Garder : au
   * clic, `x` vaut 0 et la carte partirait toujours du même côté.
   */
  exitTo?: "left" | "right";
  onSwipe?: (direction: "left" | "right") => void;
  onOpen?: () => void;
  /** « Ne plus jamais voir ce compte. » */
  onMute?: () => void;
}

export function SwipeCard({ card, depth, exitTo, onSwipe, onOpen, onMute }: SwipeCardProps) {
  const interactive = depth === 0;

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-260, 0, 260], [-14, 0, 14]);
  // Léger basculement vers l'arrière pendant le glissement : la carte « décolle ».
  const rotateX = useTransform(x, [-260, 0, 260], [6, 0, 6]);
  const passOpacity = useTransform(x, [-140, -40], [1, 0]);
  const keepOpacity = useTransform(x, [40, 140], [0, 1]);

  // Reflet spéculaire : le point chaud suit l'inclinaison, comme sur une
  // surface vernie qu'on incline sous une lampe.
  const sheenX = useTransform(x, [-260, 260], [130, -30]);
  const sheen = useMotionTemplate`radial-gradient(120% 90% at ${sheenX}% 0%, rgba(255,255,255,0.13), rgba(255,255,255,0.03) 42%, transparent 68%)`;

  function onDragEnd(_: unknown, info: PanInfo) {
    const passed =
      Math.abs(info.offset.x) > DISTANCE_THRESHOLD ||
      Math.abs(info.velocity.x) > VELOCITY_THRESHOLD;
    if (passed) onSwipe?.(info.offset.x > 0 ? "right" : "left");
  }

  return (
    <motion.article
      className="no-select absolute inset-x-0 top-0 origin-bottom"
      style={{
        zIndex: 10 - depth,
        ...(interactive ? { x, rotate, rotateX } : {}),
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
      /**
       * Entrée : la carte tombe devant soi.
       *
       * Elle part d'au-dessus (`y` négatif), vue de biais (`rotateX` négatif,
       * comme une carte qu'on voit par la tranche en la lâchant) et légèrement
       * plus grande — donc plus proche de l'œil. Elle descend, se redresse et
       * recule à sa taille de repos. C'est la combinaison des trois qui fait
       * lire une chute plutôt qu'un simple glissement vertical.
       */
      initial={{ opacity: 0, y: -170, rotateX: -34, scale: 1.07 }}
      animate={{
        opacity: depth > MAX_DEPTH ? 0 : 1,
        // Les cartes du dessous restent visibles en écaille sous celle du haut.
        y: depth * 12,
        scale: 1 - depth * 0.045,
        rotateX: 0,
      }}
      exit={(() => {
        const toRight = exitTo ? exitTo === "right" : x.get() >= 0;
        return {
          x: toRight ? 460 : -460,
          rotate: toRight ? 22 : -22,
          opacity: 0,
          transition: { duration: 0.32, ease: [0.32, 0, 0.67, 0] },
        };
      })()}
      /**
       * La carte du dessous se pose en premier, celle du dessus en dernier :
       * c'est l'ordre d'une donne réelle. Le décalage reste sous 0,1 s pour ne
       * pas rendre le deck mou entre deux swipes rapides.
       */
      transition={{ ...SPRING, delay: Math.max(0, MAX_DEPTH - depth) * DEAL_STAGGER }}
      drag={interactive ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.65}
      onDragEnd={onDragEnd}
      whileDrag={{ scale: 1.03, cursor: "grabbing" }}
    >
      <div
        className="relative flex aspect-[3/4] flex-col overflow-hidden rounded-2xl px-3 py-4"
        style={CARD_SURFACE}
      >
        {/* Reflet, au-dessus du fond mais sous le contenu. */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={interactive ? { background: sheen } : undefined}
        />

        {interactive && (
          <>
            <motion.div
              style={{ opacity: passOpacity }}
              className="pointer-events-none absolute right-4 top-4 z-20 rotate-6 rounded border-2 border-[var(--color-bad)] px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-[var(--color-bad)]"
            >
              Pass
            </motion.div>
            <motion.div
              style={{ opacity: keepOpacity }}
              className="pointer-events-none absolute left-4 top-4 z-20 -rotate-6 rounded border-2 border-[var(--color-good)] px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-[var(--color-good)]"
            >
              Garder
            </motion.div>
          </>
        )}

        <CardBody card={card} onOpen={onOpen} onMute={onMute} />
      </div>
    </motion.article>
  );
}

/**
 * Le contenu, en zones de hauteur fixe : en-tête, corps extensible, pied.
 * C'est ce découpage qui garantit que deux cartes se superposent exactement.
 */
function CardBody({
  card,
  onOpen,
  onMute,
}: {
  card: DeckCard;
  onOpen?: () => void;
  onMute?: () => void;
}) {
  const handle = card.author?.handle ?? card.author_handle ?? "?";

  return (
    <>
      <header className="relative z-10 flex shrink-0 items-center gap-2.5">
        <Avatar url={card.author?.avatar_url} size={36} />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {card.author?.display_name ?? handle}
          </p>
          <p className="truncate text-xs text-[var(--color-muted)]">
            @{handle} · {formatAge(card.age_minutes)}
          </p>
        </div>

        <ScoreGem score={card.score} size={40} />
      </header>

      <div className="relative z-10 mt-4 flex min-h-0 flex-1 flex-col">
        {card.media_url && (
          <div className="mb-2.5 h-28 overflow-hidden rounded-lg ring-1 ring-white/10">
            <img
              src={card.media_url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        {/* Troncature stricte : le format prime sur l'exhaustivité. Le texte
            complet reste accessible en ouvrant le tweet. */}
        <p
          className="overflow-hidden"
          style={{
            ...typeScaleFor(card.text, Boolean(card.media_url)),
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: card.media_url ? 5 : 9,
          }}
        >
          {card.text}
        </p>
      </div>

      <footer className="relative z-10 mt-3 shrink-0 border-t border-white/[0.07] pt-2.5">
        {card.topics.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {card.topics.slice(0, 3).map((topic) => (
              <Badge key={topic}>{topic}</Badge>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-[var(--color-muted)]">
          <span className="tabular-nums">
            ♥ {card.metrics.likes ?? 0} · ↩ {card.metrics.replies ?? 0} · ⇄{" "}
            {card.metrics.reposts ?? 0}
          </span>
          <span className="flex items-center gap-2.5">
            <Badge tone={card.source === "core" ? "core" : "discovery"}>
              {card.source === "core" ? "core" : "découverte"}
            </Badge>
            {onMute && (
              <button
                onClick={onMute}
                className="transition hover:text-[var(--color-bad)]"
                title={`Ne plus voir @${handle}`}
                aria-label={`Ne plus voir @${handle}`}
              >
                ⊘
              </button>
            )}
            {onOpen && (
              <button
                onClick={onOpen}
                className="transition hover:text-[var(--color-text)]"
                aria-label="Ouvrir le tweet sur X"
              >
                ↗
              </button>
            )}
          </span>
        </div>
      </footer>
    </>
  );
}

/**
 * Taille de police en fonction de la longueur du tweet.
 *
 * Le gabarit est fixe : un tweet de deux lignes laissait un grand vide qui se
 * lisait comme un défaut de mise en page plutôt que comme un choix. Grossir le
 * texte court occupe l'espace et donne à chaque carte l'air composée. Les
 * seuils sont volontairement peu nombreux — trois paliers suffisent, davantage
 * rendrait la pile visuellement instable.
 */
export function typeScaleFor(text: string, hasMedia: boolean): { fontSize: string; lineHeight: string } {
  if (hasMedia) return { fontSize: "15px", lineHeight: "1.35" };
  if (text.length < 90) return { fontSize: "23px", lineHeight: "1.28" };
  if (text.length < 180) return { fontSize: "19px", lineHeight: "1.32" };
  return { fontSize: "15px", lineHeight: "1.4" };
}

/**
 * Un âge en une poignée de caractères : `12 min`, `7 h`, `3 j`, `8 m`, `2 a`.
 *
 * Les deux derniers paliers sont arrivés avec l'onglet Suivis (§D49) : un
 * tweet de deck vit douze heures, mais un compte suivi peut n'avoir rien
 * publié depuis quatre ans, et « 1460 j » ne se lit pas. Les paliers du bas
 * sont inchangés — le deck et la shortlist affichent exactement la même chose
 * qu'avant.
 */
export function formatAge(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)} h`;

  const days = hours / 24;
  if (days < 31) return `${Math.round(days)} j`;

  // On arrondit AVANT de comparer : 350 jours font « 12 m », qui s'écrit « 1 a ».
  const months = Math.round(days / 30.44);
  if (months < 12) return `${months} m`;
  return `${Math.round(days / 365.25)} a`;
}
